import { isChunkLoadError } from "@/lib/chunkLoadError";
import Button from "@mui/material/Button";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error.message, errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(previous: Props) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          className="flex h-dvh lg:h-svh flex-col items-center justify-center gap-4 p-4 sm:p-8 text-center"
          role="alert"
          aria-live="assertive"
        >
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p
            id="error-description"
            className="text-sm text-muted-foreground max-w-md"
          >
            An unexpected error occurred. Please try again or contact support if
            the problem persists.
          </p>
          <Button
            variant="contained"
            onClick={this.handleRetry}
            aria-describedby="error-description"
          >
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
